import { PolicyManagementRecord } from "entities/UIModel/PolicyManagementRecord";
import { formatCurrency } from "utils/shared";
import { BookedRow } from "utils/data/enum";

export const round2 = (n: number) => Math.round(n * 100) / 100;

export const fmtPct = (n: number) => `${n.toFixed(2)}%`;

// Returns null (not 0) to distinguish "blank" from "zero".
export const toNumOrNull = (s: string) => {
  const v = s?.trim();
  if (v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// Select the right column set (calculated vs. reported) based on bookedTypeCode.
export const bookedPremium = (r: PolicyManagementRecord) => {
  return r.BookedTypeCode === "reported" ? r.ActualFee ?? 0 : r.CalculatedFee ?? 0;
}

export const bookedRemittance = (r: PolicyManagementRecord) =>
  r.BookedTypeCode === "reported"
    ? r.ActualRemittance ?? 0
    : r.CalculatedRemittance ?? 0;

// Reported/Calculated values as they're actually shown on the drawer, factoring
// in IsOverride + which side is booked. Override always writes into the Actual*
// family regardless of booked side, so when Calculated is booked, the Calculated
// section must display those Actual* numbers — that's where the override landed
// — while Reported falls back to the PreOverride snapshot of what it showed
// before Actual* got overwritten. Shared by PolicyDetailsPanel (drawer display)
// and OverrideFinancialsModal (so the modal opens pre-filled with the same
// numbers the operator is looking at, not the raw un-overridden fields).
export const financialsDisplaySource = (
  r: PolicyManagementRecord,
  bookedRow: BookedRow,
) => {
  const overrideOnCalculatedSide =
    r.IsOverride && bookedRow === BookedRow.CALCULATED;
  return {
    reported: overrideOnCalculatedSide
      ? {
          fee: r.PreOverrideActualFee,
          tax: r.PreOverrideActualPremiumTax,
          remittance: r.PreOverrideActualRemittance,
        }
      : {
          fee: r.ActualFee,
          tax: r.ActualPremiumTax,
          remittance: r.ActualRemittance,
        },
    calculated: overrideOnCalculatedSide
      ? {
          fee: r.ActualFee,
          tax: r.ActualPremiumTax,
          remittance: r.ActualRemittance,
        }
      : {
          fee: r.CalculatedFee,
          tax: r.CalculatedPremiumTax,
          remittance: r.CalculatedRemittance,
        },
  };
};

// The agency's share of a premium at a given split.
export const expectedRemittance = (premium: number, pct: number) =>
  round2(premium * (pct / 100));

// Compare in whole cents (avoiding float precision issues with 2dp figures).
// Takes resolved numbers to let callers handle premium-base decisions differently.
export const isSplitMismatch = (
  pct: number,
  premium: number,
  remittance: number,
) =>
  Math.round(expectedRemittance(premium, pct) * 100) !==
  Math.round(remittance * 100);

// Shared validation error across single-record and bulk dialogs.
export const REMITTANCE_OVER_PREMIUM_FIELD_ERROR = (premiumBase: number) =>
  `Cannot exceed New Premium (${formatCurrency(premiumBase)})`;

// Error message stating what the entered split % actually implies in dollars.
// (OverrideFinancialsModal has its own inverted version.)
export const SPLIT_MISMATCH_FIELD_ERROR = (
  pct: number,
  premiumBase: number,
  expected: number,
) =>
  `${fmtPct(pct)} of ${formatCurrency(premiumBase)} is ${formatCurrency(
    expected,
  )}`;
